import classNames from 'classnames'
import React, { MouseEventHandler } from 'react'
import { CompositionFragment, ProductFragment, ProductUnit } from '../src/model/fragments'
import { roundProductQuantity } from '../src/model/product'
import { useAdjustMultipleVariantInShoppingList } from '../src/store/shoppingListState'
import { Button } from './Button'
import { Input } from './Input'
import { Picture } from './Picture'
import { ProductList, ProductListKind } from './ProductList'
import { ProductListRowProps } from './ProductListRow'

export interface CompositionProps {
	name: React.ReactNode
	active: boolean 
	toggle: MouseEventHandler<HTMLDivElement>
	composition: CompositionFragment
}

export const Composition: React.FunctionComponent<CompositionProps> = ({
	name,
	active,
	toggle,
	composition,
}) => {
	const [quantityInput, setQuantityInput] = React.useState('')
	const quantity = parseFloat(quantityInput) || 0
	const items = React.useMemo(
		() =>
			(composition as CompositionFragment).items.filter(
				(it : {count: number, product: ProductFragment}) => it.product !== undefined && it.product !== null,
			),
		[composition],
	)
	const [prematureProductItems, setPrematureProductItems] = React.useState<
		Array<{ product: ProductListRowProps['product']; count: number }>
	>([])

	React.useEffect(() => {
		setPrematureProductItems(
			items.map((it : {count: number, product: ProductFragment}) => ({
				product: {
					...it.product,
					controlledVariantId:
						it.product?.variants.length === 1
							? it.product?.variants[0].id
							: null,
				},
				count: it.count,
			})),
		)
	}, [items])

	const onControlledVariantIdChange = React.useCallback(
		(index: number) => (variantId: null | string) => {
			setPrematureProductItems(
				prematureProductItems.map((item, innerIndex) => ({
					...item,
					product: {
						...item.product,
						controlledVariantId:
							index === innerIndex
								? variantId
								: item.product.controlledVariantId,
					},
				})),
			)
		},
		[prematureProductItems],
	)

	const productItems = React.useMemo(() => {
		return prematureProductItems.map((item, i) => {
			const unroundedControlledQuantity = quantity * (item.count / 100)
			const variant = item.product.variants[0]

			return {
				...item,
				product: {
					...item.product,
					controlledQuantity: roundProductQuantity(
						unroundedControlledQuantity,
						item.product.attributes.unit !== ProductUnit.sqm,
						(variant.attributes.palletLayerQuantity || 0) /
							(variant.attributes.palletLayerSqm || 0),
						variant.attributes.palletLayerQuantity || 0,
					).number,
					onControlledVariantIdChange: onControlledVariantIdChange(i),
				},
			}
		})
	}, [prematureProductItems, quantity, onControlledVariantIdChange])

	const adjustMultipleVariantInShoppingList =
		useAdjustMultipleVariantInShoppingList()

	const onSubmit = React.useCallback(
		(event: React.FormEvent<HTMLFormElement>) => {
			event.preventDefault()

			adjustMultipleVariantInShoppingList(
				productItems.map((item) => {
					const quantity =
						item.product.attributes.unit === ProductUnit.sqm
							? item.product.controlledQuantity
							: (item.product.variants[0].attributes.quantityPerSqm || 1) *
							  item.product.controlledQuantity;

					return {
						variantId: item.product.controlledVariantId!,
						quantity: quantity,
					}
				}),
			)
			setQuantityInput('')
		},
		[productItems, adjustMultipleVariantInShoppingList],
	)

	const highlightUnselectedVariantPickers = quantity !== 0
	const submitDisabled =
		quantity === 0 ||
		productItems.some((x) => x.product.controlledVariantId === null)

	return <>
		<section className={classNames('composition', active ? 'composition-active' : '')} onClick={toggle}>
			<h3 className="composition-title">{name}</h3>
			{composition.listingImage && (
				<div className="composition-image" >
					<Picture
						image={composition.listingImage}
						width={600}
						height={600}
						alt={name?.toLocaleString() ?? ''}
					/>
				</div>
			)}
		</section>

		{ active && 
			<div className="composition-list">
				<ProductList
					items={productItems.map((x) => x.product)}
					kind={ProductListKind.Composition}
					highlightUnselectedVariantPickers={highlightUnselectedVariantPickers}
				/>
				<form className="composition-action" onSubmit={onSubmit}>
					<h4 className="composition-action-title">Přidat do seznamu</h4>
					<div className="composition-action-in">
						<Input
							label="m²"
							type="number"
							min="0"
							value={quantityInput}
							onChange={(event) => setQuantityInput(event.target.value)}
						/>
						<Button type="submit" disabled={submitDisabled}>
							Přidat do seznamu
						</Button>
					</div>
				</form>
			</div>
		}
	</>;
}
